// ERP Sync logs: the inbox (ERP → SOC messages), the delivery log (SOC → ERP, the only replay mechanism, §6.7) and the
// digests (§6.8 reconciliation and resync answers). Each reloads after an action and on Refresh.

import { useState } from 'react';
import { api, DELIVERY_STATUSES, INBOX_STATUSES, type Delivery, type InboxMessage } from '../../api.ts';
import { Icon } from '../../components/Icon.tsx';
import { Card, Empty, ErrorBox, InlineError, JsonBlock, Loading, StatusPill } from '../../components/ui.tsx';
import { useAction, useLoad } from '../../hooks.ts';
import { useSession } from '../../session.tsx';
import { fmtLocal, relAge } from '../../time.ts';
import { clip, deliveryRes, digestHead, ErpHeader, human, inboxRes, num, plural, RefreshBtn, scopeText } from './shared.tsx';

function StatusFilter({ value, options, onChange, label }: { value: string; options: readonly string[]; onChange: (v: string) => void; label: string }) {
  return (
    <label className="field inline">
      <span className="lbl">Status</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} aria-label={label}>
        <option value="">All</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o.replace(/_/g, ' ')}
          </option>
        ))}
      </select>
    </label>
  );
}

/* ---------- inbox ---------- */

function InboxDetail({ id }: { id: string }) {
  const load = useLoad((signal) => api.erpInboxMessage(id, signal), [id]);
  const d = load.data;
  if (load.error && !d) return <ErrorBox error={load.error} onRetry={load.reload} title="Could not load the message" />;
  if (!d) return <Loading />;
  return (
    <div className="cols2">
      <div className="sec">
        <span className="lbl">Body</span>
        <JsonBlock value={d.body} maxHeight={360} />
      </div>
      <div className="sec">
        <span className="lbl">Result</span>
        {d.reason && <div className="note">{d.reason}</div>}
        <JsonBlock value={d.result} maxHeight={360} />
      </div>
    </div>
  );
}

function InboxRow({ m, open, onToggle }: { m: InboxMessage; open: boolean; onToggle: () => void }) {
  return (
    <>
      <tr className={`click${open ? ' sel' : ''}`} onClick={onToggle} aria-expanded={open}>
        <td className="mono nowrap" title={relAge(m.receivedAt)}>
          {fmtLocal(m.receivedAt)}
        </td>
        <td className="mono">{m.eventType}</td>
        <td>
          {m.entityType ? (
            <>
              {m.entityType} <span className="mono dim">{m.entityId}</span>
            </>
          ) : (
            <span className="dim">—</span>
          )}
        </td>
        <td className="mono num">v{m.version}</td>
        <td>
          <span className={`res ${inboxRes(m.status)}`}>{human(m.status)}</span>
        </td>
        <td className="small ell" title={m.reason ?? undefined}>
          {m.reason ?? <span className="dim">—</span>}
        </td>
        <td className="mono small">{m.inReplyTo ?? <span className="dim">—</span>}</td>
        <td className="small">{m.dataset ?? '—'}</td>
        <td className="mono num">{m.attempt ?? '—'}</td>
      </tr>
      {open && (
        <tr className="subrow">
          <td colSpan={9}>
            <InboxDetail id={m.eventId} />
          </td>
        </tr>
      )}
    </>
  );
}

export function InboxTab() {
  const s = useSession();
  const [status, setStatus] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const load = useLoad((signal) => api.erpInbox({ status: status || undefined, limit: 200 }, signal), [status]);
  const st = useLoad((signal) => api.erpStatus(signal), []);
  const [requeued, setRequeued] = useState<number | null>(null);
  const retry = useAction(async () => {
    const r = await api.erpRetryFailed();
    setRequeued(r.requeued);
    load.reload();
    st.reload();
  });
  const rows = load.data;
  const failed = st.data?.receiver.failed ?? (rows ?? []).filter((m) => m.status === 'failed').length;
  const reload = () => {
    load.reload();
    st.reload();
  };
  return (
    <div className="page">
      <ErpHeader tab="inbox" sub="Messages the ERP sent to the SOC, newest first. Click a row for its body and result." actions={<RefreshBtn onClick={reload} busy={load.loading} />} />
      <Card
        title={<><Icon name="inbox" />ERP → SOC messages{rows ? <span className="n">{rows.length}</span> : null}</>}
        right={
          s.isAdmin && failed > 0 ? (
            <button type="button" className="btn sm" disabled={retry.busy} onClick={() => void retry.run()}>
              <Icon name="sync" />
              {retry.busy ? 'Retrying…' : `Retry failed (${num(failed)})`}
            </button>
          ) : null
        }
      >
        <div className="filters">
          <StatusFilter value={status} options={INBOX_STATUSES} onChange={setStatus} label="Inbox status" />
          {requeued !== null && <span className="note">{plural(requeued, 'message')} requeued.</span>}
          <InlineError error={retry.error} />
        </div>
        {load.error && !rows ? (
          <ErrorBox error={load.error} onRetry={load.reload} title="Could not load the inbox" />
        ) : !rows ? (
          <Loading />
        ) : rows.length === 0 ? (
          <Empty>{status ? `No ${status.replace(/_/g, ' ')} messages.` : 'No ERP messages received yet.'}</Empty>
        ) : (
          <div className="tw">
            <table className="erp-t">
              <thead>
                <tr>
                  <th scope="col">Received</th>
                  <th scope="col">Event</th>
                  <th scope="col">Entity</th>
                  <th scope="col" className="num">Ver</th>
                  <th scope="col">Status</th>
                  <th scope="col">Reason</th>
                  <th scope="col">In reply to</th>
                  <th scope="col">Copy</th>
                  <th scope="col" className="num">Attempt</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((m) => (
                  <InboxRow key={m.eventId} m={m} open={openId === m.eventId} onToggle={() => setOpenId((v) => (v === m.eventId ? null : m.eventId))} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

/* ---------- deliveries ---------- */

function DeliveryRow({ d, canAct, onDone }: { d: Delivery; canAct: boolean; onDone: () => void }) {
  const [expand, setExpand] = useState(false);
  const [ask, setAsk] = useState<'replay' | 'discard' | null>(null);
  const act = useAction(async (what: 'replay' | 'discard') => {
    if (what === 'replay') await api.erpReplayDelivery(d.eventId);
    else await api.erpDiscardDelivery(d.eventId);
    setAsk(null);
    onDone();
  });
  const errText = d.lastError ?? d.lastBody ?? '';
  const canReplay = canAct && (d.status === 'dead' || d.status === 'discarded' || d.status === 'sent');
  const canDiscard = canAct && d.status === 'dead';
  return (
    <>
      <tr>
        <td className="mono nowrap" title={fmtLocal(d.createdAt)}>
          {relAge(d.createdAt)}
        </td>
        <td className="mono">{d.eventType}</td>
        <td>
          {d.entityType ? (
            <>
              {d.entityType} <span className="mono dim">{d.entityId}</span>
            </>
          ) : (
            <span className="dim">—</span>
          )}
        </td>
        <td className="mono num">v{d.version}</td>
        <td>
          <span className={`res ${deliveryRes(d.status)}`}>{human(d.status === 'held_until_phase6' ? 'held until cutover' : d.status)}</span>
        </td>
        <td className="mono num">{d.attempts}</td>
        <td className="mono num">{d.lastStatus ?? '—'}</td>
        <td className="small">
          {errText ? (
            <button type="button" className="linkish mono" onClick={() => setExpand((v) => !v)} aria-expanded={expand} title="Show the full text">
              {clip(errText, 60)}
            </button>
          ) : (
            <span className="dim">—</span>
          )}
        </td>
        <td className="mono nowrap small" title={fmtLocal(d.nextAttemptAt)}>
          {d.status === 'pending' && d.nextAttemptAt ? fmtLocal(d.nextAttemptAt) : '—'}
        </td>
        <td className="mono nowrap small">{fmtLocal(d.sentAt)}</td>
        <td className="nowrap">
          {(canReplay || canDiscard) && !ask && (
            <span className="btns">
              {canReplay && (
                <button type="button" className="btn sm" onClick={() => setAsk('replay')}>
                  <Icon name="sync" />
                  Replay
                </button>
              )}
              {canDiscard && (
                <button type="button" className="btn sm" onClick={() => setAsk('discard')}>
                  Discard
                </button>
              )}
            </span>
          )}
        </td>
      </tr>
      {(expand || ask) && (
        <tr className="subrow">
          <td colSpan={11}>
            {ask && (
              <div className={`confirmbar${ask === 'discard' ? ' danger' : ''}`}>
                <span>
                  {ask === 'replay'
                    ? `Send ${d.eventType} again? It goes back to pending with a fresh attempt count${d.status === 'sent' ? '; the ERP deduplicates by event id' : ''}.`
                    : 'Discard this dead message? It will not be sent. You can still replay it later.'}
                </span>
                <button type="button" className={`btn sm ${ask === 'discard' ? 'danger' : 'pri'}`} disabled={act.busy} onClick={() => void act.run(ask)}>
                  {act.busy ? 'Working…' : ask === 'replay' ? 'Yes, replay' : 'Yes, discard'}
                </button>
                <button type="button" className="btn sm" disabled={act.busy} onClick={() => setAsk(null)}>
                  Back
                </button>
                <InlineError error={act.error} />
              </div>
            )}
            {expand && (
              <div className="sec">
                {d.lastError && (
                  <>
                    <span className="lbl">Last error</span>
                    <pre className="json wrap">{d.lastError}</pre>
                  </>
                )}
                {d.lastBody && (
                  <>
                    <span className="lbl">Last response body</span>
                    <pre className="json wrap">{d.lastBody}</pre>
                  </>
                )}
                <span className="note mono">
                  {d.eventId} · first attempt {fmtLocal(d.firstAttemptAt)} · last attempt {fmtLocal(d.lastAttemptAt)}
                </span>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

export function DeliveriesTab() {
  const s = useSession();
  const [status, setStatus] = useState('');
  const load = useLoad((signal) => api.erpDeliveries({ status: status || undefined, limit: 200 }, signal), [status]);
  const rows = load.data;
  return (
    <div className="page">
      <ErpHeader
        tab="deliveries"
        sub="The SOC → ERP delivery log: every message the SOC sends, with retries and the ERP's answer. Replaying from here is the only replay mechanism."
        actions={<RefreshBtn onClick={load.reload} busy={load.loading} />}
      />
      <Card title={<><Icon name="send" />SOC → ERP delivery log{rows ? <span className="n">{rows.length}</span> : null}</>}>
        <div className="filters">
          <StatusFilter value={status} options={DELIVERY_STATUSES} onChange={setStatus} label="Delivery status" />
          {!s.isAdmin && <span className="note">Replay and discard need the admin role.</span>}
        </div>
        {load.error && !rows ? (
          <ErrorBox error={load.error} onRetry={load.reload} title="Could not load the delivery log" />
        ) : !rows ? (
          <Loading />
        ) : rows.length === 0 ? (
          <Empty>{status ? `No ${status.replace(/_/g, ' ')} messages.` : 'Nothing sent yet.'}</Empty>
        ) : (
          <div className="tw">
            <table className="erp-t">
              <thead>
                <tr>
                  <th scope="col">Created</th>
                  <th scope="col">Type</th>
                  <th scope="col">Entity</th>
                  <th scope="col" className="num">Ver</th>
                  <th scope="col">Status</th>
                  <th scope="col" className="num">Tries</th>
                  <th scope="col" className="num">HTTP</th>
                  <th scope="col">Last error / body</th>
                  <th scope="col">Next attempt</th>
                  <th scope="col">Sent</th>
                  <th scope="col">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((d) => (
                  <DeliveryRow key={d.eventId} d={d} canAct={s.isAdmin} onDone={load.reload} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

/* ---------- digests ---------- */

const DIFF_KIND: Record<string, string> = {
  missing: 'Missing in SOC',
  newer_in_erp: 'Newer in ERP',
  hash_mismatch: 'Hash mismatch',
  version_went_backwards: 'Version went backwards',
  missed_delete: 'Missed delete',
  vanished: 'Vanished',
  held: 'Held',
  dead_lettered: 'Dead-lettered (reported)',
};

function DigestDetailCard({ id, onClose }: { id: string; onClose: () => void }) {
  const load = useLoad((signal) => api.erpDigest(id, signal), [id]);
  const d = load.data;
  const diffs = d?.diffs ?? [];
  return (
    <Card
      title={<><Icon name="list" />Digest <span className="mono small">{id}</span></>}
      right={
        <button type="button" className="btn sm ghost" onClick={onClose}>
          Close
        </button>
      }
    >
      {load.error && !d ? (
        <ErrorBox error={load.error} onRetry={load.reload} title="Could not load the digest" />
      ) : !d ? (
        <Loading />
      ) : (
        <>
          {d.summary && (
            <div className="erp-kv">
              <span><span className="lbl">Entities</span> <b>{num(d.summary.entities)}</b></span>
              <span><span className="lbl">In sync</span> <b>{num(d.summary.inSync)}</b></span>
              <span><span className="lbl">Missing</span> <b>{num(d.summary.missing)}</b></span>
              <span><span className="lbl">Newer in ERP</span> <b>{num(d.summary.newerInErp)}</b></span>
              <span><span className="lbl">Hash mismatch</span> <b>{num(d.summary.hashMismatch)}</b></span>
              <span><span className="lbl">Went backwards</span> <b>{num(d.summary.versionWentBackwards)}</b></span>
              <span><span className="lbl">Missed deletes</span> <b>{num(d.summary.missedDeletes)}</b></span>
              <span><span className="lbl">Vanished</span> <b>{num(d.summary.vanished)}</b></span>
              <span><span className="lbl">Held</span> <b>{num(d.summary.held)}</b></span>
              <span><span className="lbl">Resync asked</span> <b>{num(d.summary.resyncRequested)}</b></span>
              <span><span className="lbl">Copy</span> <b>{d.summary.dataset}</b></span>
            </div>
          )}
          {d.partial && <div className="note warn-t">Partial: some parts never arrived, so absences were not treated as deletions.</div>}
          {diffs.length === 0 ? (
            <Empty>{d.status === 'compared' ? 'No differences.' : 'Not compared yet.'}</Empty>
          ) : (
            <div className="tw">
              <table className="erp-t">
                <thead>
                  <tr>
                    <th scope="col">Entity</th>
                    <th scope="col">Kind</th>
                    <th scope="col" className="num">ERP ver</th>
                    <th scope="col" className="num">SOC ver</th>
                    <th scope="col">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {diffs.map((x, i) => (
                    <tr key={`${x.entityType}:${x.entityId}:${i}`}>
                      <td>
                        {x.entityType} <span className="mono dim">{x.entityId}</span>
                      </td>
                      <td>
                        <span className={`res ${x.kind === 'held' || x.kind === 'dead_lettered' ? 'wait' : x.kind === 'version_went_backwards' || x.kind === 'vanished' ? 'bad' : 'skip'}`}>{DIFF_KIND[x.kind] ?? human(x.kind)}</span>
                      </td>
                      <td className="mono num">{x.erpVersion ?? '—'}</td>
                      <td className="mono num">{x.socVersion ?? '—'}</td>
                      <td className="small">{x.action === 'none' ? <span className="dim">none</span> : x.action}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </Card>
  );
}

export function DigestsTab() {
  const load = useLoad((signal) => api.erpDigests(signal), []);
  const [openId, setOpenId] = useState<string | null>(null);
  const rows = load.data;
  return (
    <div className="page">
      <ErpHeader
        tab="digests"
        sub="The ERP's nightly digest and its answers to resync requests, compared entity by entity with the replica (contract §6.8)."
        actions={<RefreshBtn onClick={load.reload} busy={load.loading} />}
      />
      <Card title={<><Icon name="db" />Digests{rows ? <span className="n">{rows.length}</span> : null}</>}>
        {load.error && !rows ? (
          <ErrorBox error={load.error} onRetry={load.reload} title="Could not load digests" />
        ) : !rows ? (
          <Loading />
        ) : rows.length === 0 ? (
          <Empty>No digests received yet.</Empty>
        ) : (
          <div className="tw">
            <table className="erp-t">
              <thead>
                <tr>
                  <th scope="col">As of</th>
                  <th scope="col">Scope</th>
                  <th scope="col">Request</th>
                  <th scope="col" className="num">Parts</th>
                  <th scope="col">Status</th>
                  <th scope="col">Summary</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((d) => {
                  const h = d.summary ? digestHead(d.comparedAt ?? d.asOf, d.summary) : null;
                  return (
                      <tr key={d.digestId} className={`click${openId === d.digestId ? ' sel' : ''}`} onClick={() => setOpenId((v) => (v === d.digestId ? null : d.digestId))}>
                        <td className="mono nowrap">{fmtLocal(d.asOf)}</td>
                        <td className="small">{scopeText(d.scope)}</td>
                        <td className="mono small">{d.requestId ?? <span className="dim">nightly</span>}</td>
                        <td className={`mono num${d.parts < d.partCount ? ' warn-t' : ''}`}>
                          {d.parts}/{d.partCount}
                        </td>
                        <td>
                          <StatusPill status={d.status === 'compared' ? (d.partial ? 'partial' : 'compared') : d.status} />
                        </td>
                        <td className={`small${h && !h.ok ? ' warn-t' : ''}`}>{h ? `${h.head} · ${h.tail}` : <span className="dim">—</span>}</td>
                      </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      {openId && <DigestDetailCard id={openId} onClose={() => setOpenId(null)} />}
    </div>
  );
}
